import * as React from "react";
import { Row, Col, Popconfirm, Select, InputNumber, Tooltip, Form } from "antd";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { EditableTable, EditableColumnProps } from "Components/General/EditableTable";
import { SelectProps } from "antd/lib/select";
import { Button } from "Components/General/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { collectionFolderStore } from "Pages/Project/Documents/CollectionFolder/CollectionFolderStore";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";

interface FilesProps extends WithTranslation {
  noCopies?: { [filename: string]: number };
  files: API_PrintJobFile[];
}

interface FilesSelectionProps extends WithTranslation {
  value?: API_PrintJobFile[];
  onChange?: (value: API_PrintJobFile[]) => API_PrintJobFile[];
  data: API_PrintJobFile[];
  noCopies?: { [filename: string]: number };
}

interface FilesSelectionState {
  data: API_PrintJobFile[];
  commonValues: { [key: string]: string | number };
}

interface FileOptionsSelectionProps extends WithTranslation {
  data: Array<{ value: string; name: string }>;
  defaultValue: string;
  onChange: (v: string) => void;
}

const FileOptionsSelection = withTranslation(I18N_NS.PROJECT)(
  class extends React.Component<FileOptionsSelectionProps, SelectProps<string>> {
    render() {
      const { data, t, onChange, defaultValue, ...selectProps } = this.props;
      return (
        <Select value={defaultValue} onChange={(value) => onChange(value as string)} {...selectProps}>
          {data.map(({ value, name }) => (
            <Select.Option key={name} value={value}>
              {t(name)}
            </Select.Option>
          ))}
        </Select>
      );
    }
  }
);

export const FilesSelection = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<FilesSelectionProps, FilesSelectionState> {
      private folding: Array<{ value: API_PRINTJOBFILE_FOLDING; name: string }> = [
        { value: "ROLLED", name: "rolled" },
        { value: "FILLING_20_MM", name: "filling margin 20 mm" },
        { value: "NO_FILLING", name: "no filling margin" },
        { value: "FILLING_STRIP", name: "filling strip" },
      ];

      private paper: Array<{ value: API_PRINTJOBFILE_PLOTTER; name: string }> = [
        { value: "BLACK_WHITE", name: "b/w on paper" },
        { value: "COLORED", name: "coloured on paper" },
        { value: "ORIGINAL", name: "like original on paper" },
        { value: "TRANSPARENT", name: "b/w on transparent" },
      ];

      constructor(props: FilesSelectionProps) {
        super(props);
        this.state = {
          data: props.data,
          commonValues: {
            copies: 1,
            scale: 100,
          },
        };
      }

      addToCollectionFolder = ({ name, id }: API_PrintJobFile) => {
        const { t } = this.props;
        collectionFolderStore.addFile({ name, id, tag: "PRINT JOB" });
        notificationSuccess(t("Success"), t("Added to collection folder"));
      };

      get columns(): EditableColumnProps<API_PrintJobFile>[] {
        const { t, noCopies = {} } = this.props;
        return [
          {
            title: t("File"),
            dataIndex: "name",
            editable: false,
            className: "maxWidth-overflow-cell",
          },
          {
            title: t("Copies") + " *",
            width: 80,
            dataIndex: "copies",
            editable: true,
            inputType: "number",
            inputProps: {
              min: 1,
            },
            formItemProps: {
              rules: [{ required: true, message: t("Please provide a number") }],
            },
            render: (text, record) => {
              const message =
                typeof noCopies[record.name] === "number"
                  ? noCopies[record.name] <= 0
                    ? t("No further copies available for this invoice recipient")
                    : t("Maximum of {{copies}} copies available for this invoice recipient", {
                        copies: noCopies[record.name],
                      })
                  : null;

              return typeof noCopies[record.name] === "number" ? (
                <Tooltip title={t(message, { copies: noCopies[record.name] })}>
                  <b className="pointer" style={{ color: "red" }}>
                    {text}
                  </b>
                </Tooltip>
              ) : (
                text
              );
            },
          },
          {
            title: t("Colours and paper"),
            width: 160,
            dataIndex: "plotter",
            editable: true,
            inputType: "custom",
            customInput: FileOptionsSelection,
            inputProps: { data: this.paper },
            render: (text, record) => (record.plotter ? t(this.paper.find((plot) => plot.value === text).name) : null),
            formItemProps: {
              rules: [{ required: true, message: t("Please make a choice") }],
            },
          },
          {
            title: t("Folding"),
            width: 160,
            dataIndex: "folding",
            editable: true,
            inputType: "custom",
            customInput: FileOptionsSelection,
            inputProps: { data: this.folding },
            render: (text, record) =>
              record.folding ? t(this.folding.find((fold) => fold.value === text).name) : null,
            formItemProps: {
              rules: [{ required: true, message: t("Please make a choice") }],
            },
          },
          {
            title: t("Scale"),
            width: 90,
            dataIndex: "scale",
            editable: true,
            inputType: "number",
            inputProps: {
              formatter: (value) => `${value}%`,
              parser: (value) => value.replace("%", ""),
              min: 1,
            },
            formItemProps: {
              rules: [{ required: true, message: t("Please provide percentage") }],
            },
            render: (text, record) => {
              return record.scale >= 1 ? `${record.scale}%` : null;
            },
          },
          {
            title: "",
            dataIndex: "operation",
            width: 60,
            editable: false,
            render: (text, record) =>
              this.state.data.length >= 1 ? (
                <>
                  {!isNaN(noCopies[record.name]) ? (
                    <span style={{ marginRight: 5 }} onClick={() => this.addToCollectionFolder(record)}>
                      <FontAwesomeIcon icon={["far", "folder"]} fixedWidth={true} />
                    </span>
                  ) : null}
                  <Popconfirm title={t("Are you sure?")} onConfirm={() => this.handleDelete(record.id)}>
                    <FontAwesomeIcon icon={["far", "trash-alt"]} fixedWidth={true} />
                  </Popconfirm>
                </>
              ) : null,
          },
        ];
      }

      handleDelete = (id) => {
        const { onChange } = this.props;
        const data = this.state.data.filter((item) => item.id !== id);

        this.setState({ data }, () => onChange(this.state.data));
      };

      handleEdit = async (obj: API_PrintJobFile) => {
        const { onChange } = this.props;
        const data = [...this.state.data];
        try {
          const index = data.findIndex((item) => item.id === obj.id);
          if (index !== -1) {
            data[index] = { ...obj, name: data[index].name };
          }

          this.setState({ data }, () => onChange(this.state.data));
        } catch (error) {
          throw error;
        }
      };

      handleApllyToAll = async () => {
        const { commonValues } = this.state;
        const { onChange } = this.props;

        try {
          const data = this.state.data.map((file) => {
            return { ...file, ...commonValues };
          });

          this.setState({ data, commonValues: {} }, () => onChange(this.state.data));
        } catch (error) {
          throw error;
        }
      };

      render() {
        const { t } = this.props;

        return (
          <>
            <Row justify="start" align="middle" gutter={16}>
              <Col span={3}>
                <Form.Item label={t("Copies")}>
                  <InputNumber
                    value={this.state.commonValues.copies as number}
                    min={1}
                    onChange={(value) =>
                      this.setState({
                        commonValues: { ...this.state.commonValues, copies: value },
                      })
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={t("Plotter")}>
                  <FileOptionsSelection
                    defaultValue={this.state.commonValues.plotter as string}
                    data={this.paper}
                    onChange={(value) =>
                      this.setState({
                        commonValues: { ...this.state.commonValues, plotter: value },
                      })
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item label={t("Folding")}>
                  <FileOptionsSelection
                    defaultValue={this.state.commonValues.folding as string}
                    data={this.folding}
                    onChange={(value) =>
                      this.setState({
                        commonValues: { ...this.state.commonValues, folding: value },
                      })
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={3}>
                <Form.Item label={t("Scale")}>
                  <InputNumber
                    min={1}
                    value={this.state.commonValues.scale as number}
                    prefix={"%"}
                    onChange={(value) =>
                      this.setState({
                        commonValues: { ...this.state.commonValues, scale: value },
                      })
                    }
                  />
                </Form.Item>
              </Col>
              <Form.Item label={t(" ")}>
                <Button style={{ flexGrow: 1 }} type="primary" faIcon={["fas", "plus"]} onClick={this.handleApllyToAll}>
                  {t("Set for all positions")}
                </Button>
              </Form.Item>
            </Row>
            <EditableTable<API_PrintJobFile>
              name="printJobFilesEdit"
              sortDirections={["ascend", "descend"]}
              dataSource={this.state.data}
              rowKey={({ id }) => (Math.random() + id).toString(36)}
              rowIdKey="id"
              columns={this.columns}
              onEdit={this.handleEdit}
            />
          </>
        );
      }
    }
  )
);

export const Files = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<FilesProps> {
      render() {
        const { files, t } = this.props;

        return (
          <Row justify="start" align="middle" gutter={24}>
            <Col span={24}>
              <Form.Item name="files" initialValue={files}>
                <FilesSelection data={files} noCopies={this.props.noCopies} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <span style={{ fontSize: "13px", color: "#8992a1" }}>
                {/* tslint:disable-next-line:max-line-length */}* {t("Copies are per user, not total")}
              </span>
            </Col>
          </Row>
        );
      }
    }
  )
);
